<template>
  <div class="ht-loader" :class="`ht-loader--${size}`" role="status" aria-live="polite">
    <div class="ht-loader__stage">
      <span class="ht-loader__glow"></span>
      <span class="ht-loader__ring ht-loader__ring--outer"></span>
      <span class="ht-loader__ring ht-loader__ring--inner"></span>
      <span class="ht-loader__plus ht-loader__plus--a"></span>
      <span class="ht-loader__plus ht-loader__plus--b"></span>
      <div class="ht-loader__core">
        <svg class="ht-loader__heart" viewBox="0 0 64 64" aria-hidden="true">
          <path
            class="ht-loader__heart-body"
            d="M32 57C10 41 4 29 4 20.5C4 11.5 11 6 18.5 6C24 6 29 9 32 14C35 9 40 6 45.5 6C53 6 60 11.5 60 20.5C60 29 54 41 32 57Z"
          />
          <path
            class="ht-loader__heart-shine"
            d="M13 19C13 14 17 11 21 11C23.5 11 25.5 12 27 13.5C23 12.5 17.5 13.5 15 20C14 22 13 21 13 19Z"
          />
          <path class="ht-loader__ecg-track" pathLength="100" d="M9 31H21L26 23L32 41L38 19L43 31H55" />
          <path class="ht-loader__ecg-line" pathLength="100" d="M9 31H21L26 23L32 41L38 19L43 31H55" />
        </svg>
      </div>
    </div>

    <template v-if="message">
      <div class="ht-loader__message">
        {{ cleanMessage }}<span class="ht-loader__dots" aria-hidden="true"><i>.</i><i>.</i><i>.</i></span>
      </div>
    </template>
    <span v-else class="sr-only">Loading</span>
  </div>
</template>

<script>
// Health loader: two counter-rotating gradient rings around a beating heart with an ECG trace,
// a soft breathing glow, floating medical crosses.
// Presentation only. size: "lg" (page / table area), "md", "sm" (compact panels).
export default {
  name: "HealthLoader",
  props: {
    message: { type: String, default: "" },
    size: { type: String, default: "lg" },
  },
  computed: {
    // "Loading doctors..." -> "Loading doctors" (animated dots replace the literal ellipsis)
    cleanMessage() {
      return this.message.replace(/\s*(\.{3}|…)\s*$/, "");
    },
  },
};
</script>

<style scoped>
/* Every animation here is transform / opacity only, so it is composited on the GPU and
   stays smooth even while the page is busy rendering data. */
.ht-loader {
  --s: 112px; /* stage size */
  --ring: 5px; /* ring thickness */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 28px 12px;
}

.ht-loader--md {
  --s: 80px;
  --ring: 4px;
  gap: 14px;
  padding: 20px 12px;
}

.ht-loader--sm {
  --s: 52px;
  --ring: 3px;
  gap: 10px;
  padding: 12px;
}

.ht-loader__stage {
  position: relative;
  width: var(--s);
  height: var(--s);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* soft halo that breathes behind everything */
.ht-loader__glow {
  position: absolute;
  inset: -22%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(108, 184, 220, 0.38) 0%, rgba(77, 112, 130, 0.14) 45%, rgba(77, 112, 130, 0) 70%);
  will-change: transform, opacity;
  animation: ht-breathe 2.6s ease-in-out infinite;
}

/* rings: a gradient arc (conic) cut into a ring with a mask, spun with transform */
.ht-loader__ring {
  position: absolute;
  border-radius: 50%;
  will-change: transform;
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - var(--ring)), #000 calc(100% - var(--ring) + 1px));
  mask: radial-gradient(farthest-side, transparent calc(100% - var(--ring)), #000 calc(100% - var(--ring) + 1px));
}

.ht-loader__ring--outer {
  inset: 0;
  background: conic-gradient(from 0deg, rgba(77, 112, 130, 0) 0%, rgba(77, 112, 130, 0) 35%, #6cb8dc 80%, #4d7082 100%);
  animation: ht-spin 1.3s linear infinite;
}

.ht-loader__ring--inner {
  inset: 17%;
  background: conic-gradient(from 180deg, rgba(108, 184, 220, 0) 0%, rgba(108, 184, 220, 0) 45%, rgba(108, 184, 220, 0.9) 100%);
  animation: ht-spin 2.1s linear infinite reverse;
}

/* bright head on the leading edge of the outer arc */
.ht-loader__ring--outer::after {
  content: "";
  position: absolute;
  top: 0;
  left: 50%;
  width: var(--ring);
  height: var(--ring);
  margin-left: calc(var(--ring) / -2);
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 10px 3px rgba(108, 184, 220, 0.95);
}

/* white core carrying the brand mark */
.ht-loader__core {
  position: relative;
  width: 64%;
  height: 64%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  box-shadow: 0 8px 22px rgba(77, 112, 130, 0.28), inset 0 0 0 1px rgba(77, 112, 130, 0.1);
  will-change: transform;
  animation: ht-pulse 2.6s ease-in-out infinite;
}

.ht-loader__heart {
  width: 66%;
  height: 66%;
  overflow: visible;
  transform-origin: 50% 55%;
  will-change: transform;
  animation: ht-lubdub 1.6s ease-in-out infinite;
}

.ht-loader__heart-body {
  fill: #4d7082;
}

.ht-loader__heart-shine {
  fill: #ffffff;
  opacity: 0.28;
}

.ht-loader__ecg-track,
.ht-loader__ecg-line {
  fill: none;
  stroke: #ffffff;
  stroke-width: 3.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.ht-loader__ecg-track {
  opacity: 0.3;
}

/* the trace is "read" across the heart, held, then fades before the next beat */
.ht-loader__ecg-line {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: ht-read 1.6s cubic-bezier(0.45, 0, 0.25, 1) infinite;
}

/* small medical crosses drifting up around the rings */
.ht-loader__plus {
  position: absolute;
  width: calc(var(--s) * 0.11);
  height: calc(var(--s) * 0.11);
  opacity: 0;
  will-change: transform, opacity;
}

.ht-loader__plus::before,
.ht-loader__plus::after {
  content: "";
  position: absolute;
  background: #6cb8dc;
  border-radius: 2px;
}

.ht-loader__plus::before {
  left: 38%;
  right: 38%;
  top: 0;
  bottom: 0;
}

.ht-loader__plus::after {
  top: 38%;
  bottom: 38%;
  left: 0;
  right: 0;
}

.ht-loader__plus--a {
  top: -4%;
  right: -6%;
  animation: ht-float 3.2s ease-in-out infinite;
}

.ht-loader__plus--b {
  bottom: 2%;
  left: -8%;
  animation: ht-float 3.2s ease-in-out 1.6s infinite;
}

.ht-loader__message {
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--ht-text, #1f2d3d);
}

.ht-loader--sm .ht-loader__message {
  font-size: 0.75rem;
}

.ht-loader__dots i {
  font-style: normal;
  display: inline-block;
  animation: ht-dot 1.4s infinite both;
}

.ht-loader__dots i:nth-child(2) {
  animation-delay: 0.2s;
}

.ht-loader__dots i:nth-child(3) {
  animation-delay: 0.4s;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

@keyframes ht-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes ht-breathe {
  0%,
  100% {
    transform: scale(0.92);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.08);
    opacity: 1;
  }
}

@keyframes ht-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.045);
  }
}

@keyframes ht-lubdub {
  0%,
  100% {
    transform: scale(1);
  }
  14% {
    transform: scale(1.14);
  }
  28% {
    transform: scale(1);
  }
  42% {
    transform: scale(1.09);
  }
  60% {
    transform: scale(1);
  }
}

@keyframes ht-read {
  0% {
    stroke-dashoffset: 100;
    opacity: 1;
  }
  55% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  85% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  100% {
    stroke-dashoffset: 0;
    opacity: 0;
  }
}

@keyframes ht-float {
  0% {
    transform: translateY(6px) scale(0.7);
    opacity: 0;
  }
  35% {
    opacity: 0.9;
  }
  100% {
    transform: translateY(-12px) scale(1);
    opacity: 0;
  }
}

@keyframes ht-dot {
  0%,
  80%,
  100% {
    opacity: 0.2;
  }
  40% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ht-loader__ring,
  .ht-loader__glow,
  .ht-loader__core,
  .ht-loader__heart,
  .ht-loader__plus {
    animation: none;
  }

  .ht-loader__ecg-line {
    animation: none;
    stroke-dashoffset: 0;
  }
}
</style>
